import { useState, useEffect, useRef, useCallback } from 'react';
import { MarketCoin } from '../types';
import { cgGet } from '../utils/coingecko';

const CACHE_KEY = 'mycryptoplanner_market_v1';
const CACHE_TTL = 60 * 1_000;
const REFRESH_INTERVAL = 60 * 1_000;

/** CoinGecko markets: 100 per page, pages 1–5 (the top 500 coins). */
export const MAX_MARKET_PAGES = 5;
const PER_PAGE = 100;

interface MarketCache {
  coins: MarketCoin[];
  fetchedAt: number;
}

function loadCache(): MarketCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as MarketCache) : null;
  } catch { return null; }
}

function saveCache(coins: MarketCoin[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ coins, fetchedAt: Date.now() }));
  } catch { /* ignore */ }
}

export function useMarketCoins(page: number) {
  const cached = loadCache(); // page-1 cache only

  // Seed from the page-1 cache only when we mount on page 1; higher pages start
  // empty + loading so page-1 data never flashes under a higher page.
  const [coins,       setCoins]       = useState<MarketCoin[]>(
    page === 1 && cached ? cached.coins : []
  );
  const [loading,     setLoading]     = useState<boolean>(!(page === 1 && cached));
  const [error,       setError]       = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(
    page === 1 && cached ? new Date(cached.fetchedAt) : null
  );

  // Mirror the current page in a ref so the interval / manual refresh always
  // fetch the CURRENTLY displayed page rather than a stale captured value.
  const pageRef  = useRef(page);
  pageRef.current = page;
  const fetchRef = useRef<(() => Promise<void>) | null>(null);

  const fetch = useCallback(async () => {
    const p = pageRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await cgGet('/coins/markets', {
        params: {
          vs_currency: 'usd',
          order: 'market_cap_desc',
          per_page: PER_PAGE,
          page: p,
          sparkline: true,
          price_change_percentage: '24h,7d',
        },
        timeout: 15_000,
      });

      const mapped: MarketCoin[] = (data as Record<string, unknown>[]).map((c) => ({
        id:             String(c.id),
        symbol:         String(c.symbol).toUpperCase(),
        name:           String(c.name),
        image:          String(c.image),
        marketCapRank:  Number(c.market_cap_rank) || 0,
        currentPrice:   Number(c.current_price)   || 0,
        marketCap:      Number(c.market_cap)       || 0,
        volume24h:      Number(c.total_volume)     || 0,
        change24h:      Number(c.price_change_percentage_24h)            || 0,
        change7d:       Number(c.price_change_percentage_7d_in_currency) || 0,
        sparkline7d:    ((c.sparkline_in_7d as { price?: number[] })?.price ?? []),
      }));

      // Only page 1 is cached — pages 2+ carry sparkline arrays that would blow
      // the localStorage quota, so they always fetch fresh and are never stored.
      if (p === 1) saveCache(mapped);
      setCoins(mapped);
      setLastUpdated(new Date());
      setError(null);
    } catch {
      // Cache fallback exists for page 1 only; other pages surface the error.
      const stale = p === 1 ? loadCache() : null;
      if (stale) {
        setCoins(stale.coins);
        setLastUpdated(new Date(stale.fetchedAt));
        setError('Showing cached data. Refresh to retry.');
      } else {
        setError('Failed to load market data. Check your connection.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  fetchRef.current = fetch;

  // Fetch on mount and whenever the page changes; auto-refresh the current page
  // every 60s. Page 1 with a fresh cache skips the initial network call.
  useEffect(() => {
    if (page === 1) {
      const c = loadCache();
      if (c && Date.now() - c.fetchedAt < CACHE_TTL) {
        setCoins(c.coins);
        setLastUpdated(new Date(c.fetchedAt));
        setError(null);
        setLoading(false);
      } else {
        fetch();
      }
    } else {
      fetch(); // pages 2+ always fetch fresh (never cached)
    }
    const id = setInterval(() => { fetchRef.current?.(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return { coins, loading, error, lastUpdated, refresh: fetch };
}
